// LAP-646 — Empty state enrichi pour le Social Dashboard.
//
// Quand l'utilisateur n'a pas encore de contacts ou d'événements liés, on
// remplace le label générique « Pas encore de données sociales » par un
// message explicatif (titre + description courte) + 1 ou 2 boutons d'action
// qui le redirigent vers les bons écrans pour générer de la donnée :
//   - "Ajouter un contact" → /MesContacts
//   - "Créer un événement" → /MonCalendrier
//
// Cette aide est rendue à l'intérieur de chaque carte du dashboard ; elle se
// substitue à l'EmptyState générique (Investments/_shared/EmptyState) pour
// laisser le ton du dashboard plus actionnable.

import React from 'react'
import { Box, Stack, Button } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined'

// Liste de routes les plus utiles pour générer les données du dashboard.
const ROUTES = {
  contacts: '/MesContacts',
  calendar: '/MonCalendrier',
}

const ACTION_ICON = {
  contacts: <PersonAddOutlinedIcon fontSize="small" />,
  calendar: <EventOutlinedIcon fontSize="small" />,
}

export default function EmptyHelp({
  icon,
  title,
  description,
  // Liste d'actions : [{ key: 'contacts'|'calendar'|'custom', label, route? }]
  actions = [{ key: 'contacts' }, { key: 'calendar' }],
}) {
  const navigate = useNavigate()

  return (
    <Stack
      height="100%"
      width="100%"
      alignItems="center"
      justifyContent="center"
      spacing={1.25}
      sx={{ color: 'rgba(255,255,255,0.55)', textAlign: 'center', px: 2, py: 2 }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          backgroundColor: 'rgba(255,255,255,0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'rgba(255,255,255,0.65)',
        }}
      >
        {icon || <InboxOutlinedIcon sx={{ fontSize: 22, opacity: 0.7 }} />}
      </Box>
      {title && (
        <Box sx={{ fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}>
          {title}
        </Box>
      )}
      {description && (
        <Box sx={{ fontSize: 12.5, maxWidth: 320, lineHeight: 1.5 }}>
          {description}
        </Box>
      )}
      {actions.length > 0 && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          mt={0.5}
          sx={{ width: '100%', justifyContent: 'center' }}
        >
          {actions.map((a, idx) => (
            <Button
              key={a.key + idx}
              size="small"
              variant={idx === 0 ? 'contained' : 'outlined'}
              startIcon={ACTION_ICON[a.key]}
              onClick={() => navigate(a.route || ROUTES[a.key])}
              sx={{
                textTransform: 'none',
                fontSize: 12.5,
                borderRadius: 1.5,
                ...(idx === 0
                  ? {
                      backgroundColor: 'rgba(99,160,255,0.85)',
                      color: '#001b42',
                      '&:hover': { backgroundColor: 'rgba(99,160,255,1)' },
                    }
                  : {
                      borderColor: 'rgba(255,255,255,0.15)',
                      color: 'rgba(255,255,255,0.85)',
                      '&:hover': {
                        borderColor: 'rgba(255,255,255,0.35)',
                        backgroundColor: 'rgba(255,255,255,0.04)',
                      },
                    }),
              }}
            >
              {a.label}
            </Button>
          ))}
        </Stack>
      )}
    </Stack>
  )
}
